import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellRing, Check } from 'lucide-react';
import { ALERT_TYPE_LABELS, type AlertDto } from '@brimostech/shared';
import { Badge, Button, EmptyState, Skeleton } from '@brimostech/ui';
import { api } from '@/lib/api-client';
import { reportError } from '@/lib/api-errors';
import { formatDateTime, formatRelative } from '@/lib/format';

const SEVERITY: Record<
  AlertDto['severity'],
  { label: string; variant: 'muted' | 'warning' | 'error' }
> = {
  info: { label: 'info', variant: 'muted' },
  warning: { label: 'atenção', variant: 'warning' },
  critical: { label: 'crítico', variant: 'error' },
};

interface AlertListProps {
  alerts: AlertDto[] | undefined;
  isLoading: boolean;
  canResolve: boolean;
  /** Route prefix used to resolve alerts (`/alerts` or `/admin/alerts`). */
  resolvePath: string;
  showCondominium?: boolean;
  emptyDescription?: string;
}

/** Alert cards with severity, age and a resolve action. */
export function AlertList({
  alerts,
  isLoading,
  canResolve,
  resolvePath,
  showCondominium,
  emptyDescription,
}: AlertListProps) {
  const queryClient = useQueryClient();
  const resolve = useMutation({
    mutationFn: (id: string) => api<AlertDto>(`${resolvePath}/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (error) => reportError(error),
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (!alerts || alerts.length === 0) {
    return (
      <EmptyState
        icon={<BellRing />}
        title="Nenhum alerta"
        description={emptyDescription ?? 'Gateways e dispositivos estão respondendo normalmente.'}
      />
    );
  }
  return (
    <ul className="space-y-2">
      {alerts.map((alert) => (
        <li
          key={alert.id}
          className="flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-card p-4"
        >
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={SEVERITY[alert.severity].variant}>
                {SEVERITY[alert.severity].label}
              </Badge>
              <span className="font-medium">{alert.title}</span>
              <span className="text-xs text-muted-foreground">
                {ALERT_TYPE_LABELS[alert.type] ?? alert.type}
              </span>
              {alert.status === 'resolved' ? <Badge variant="muted">resolvido</Badge> : null}
            </div>
            <p className="text-sm text-muted-foreground">{alert.message}</p>
            <p className="text-xs text-muted-foreground">
              {showCondominium && alert.condominiumName ? `${alert.condominiumName} · ` : ''}
              <span title={formatDateTime(alert.createdAt)}>{formatRelative(alert.createdAt)}</span>
              {alert.resolvedAt ? ` · resolvido ${formatRelative(alert.resolvedAt)}` : ''}
            </p>
          </div>
          {canResolve && alert.status === 'open' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate(alert.id)}
            >
              <Check /> Resolver
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
